import { useCallback, useEffect, useRef, useState } from "react";

import type { TrackResponse } from "@sirdab/returns-shared";
import { Spinner } from "@sirdab/ui";

import { useApi } from "../api";
import { ErrorState } from "../components";
import { useFlow } from "../flow";
import { useMessages, usePortalCopy } from "../i18n";
import { LookupScreen } from "./LookupScreen";
import { TrackScreen } from "./TrackScreen";

/**
 * `/:merchantSlug/returns/r/:token`: the track link minted when a return is created.
 *
 * The token opens that one return's tracking view and nothing else, so this screen makes
 * one `resolve-link` call and hands the answer to `TrackScreen`.
 *
 * A 404 is never explained. The endpoint answers the same way for a token that expired,
 * one that was replaced and one that never existed, so this falls back to the ordinary
 * lookup with one friendly line and no diagnosis. Any other failure (offline, a server
 * hiccup) says nothing about the link and offers a retry.
 */
type Resolution =
  | { readonly state: "loading" }
  | { readonly state: "ready"; readonly data: TrackResponse }
  | { readonly state: "dead" }
  | { readonly state: "unreachable" };

export function DeepLinkScreen({
  token,
}: {
  token: string;
}): React.JSX.Element {
  const api = useApi();
  const { t } = useMessages();
  const c = usePortalCopy();
  const { merchantSlug } = useFlow();

  const [resolution, setResolution] = useState<Resolution>({
    state: "loading",
  });
  const live = useRef(true);

  const resolve = useCallback(async (): Promise<void> => {
    setResolution({ state: "loading" });
    const result = await api.resolveLink({ merchantSlug, token });
    if (!live.current) return;
    if (result.ok) {
      setResolution({ state: "ready", data: result.data });
      return;
    }
    setResolution(
      result.kind === "response" && result.status === 404
        ? { state: "dead" }
        : { state: "unreachable" },
    );
  }, [api, merchantSlug, token]);

  // Once per mount: StrictMode runs effects twice in development, and one page load
  // should make one call.
  const started = useRef(false);
  useEffect(() => {
    live.current = true;
    if (!started.current) {
      started.current = true;
      void resolve();
    }
    return () => {
      live.current = false;
    };
  }, [resolve]);

  if (resolution.state === "ready") {
    return (
      <TrackScreen
        merchantSlug={merchantSlug}
        linked={{ token, data: resolution.data }}
      />
    );
  }
  if (resolution.state === "dead") return <LookupScreen linkFailed />;

  return (
    <section className="flex flex-col gap-4">
      <header className="border-border flex flex-col gap-1 border-b pb-4">
        <h1 className="text-heading-sm text-foreground">
          {t("screen.track.title")}
        </h1>
      </header>
      {resolution.state === "unreachable" ? (
        <ErrorState
          message={c("link.unreachable")}
          retryLabel={t("action.retry")}
          onRetry={() => void resolve()}
        />
      ) : (
        <p
          role="status"
          className="text-body text-muted-foreground flex items-center gap-2"
        >
          <Spinner />
          {c("link.resolving")}
        </p>
      )}
    </section>
  );
}
